/**
 * AP-36 7.1–7.3 — die **Speicherstelle**: eine je Datensatz (Projekt, Position,
 * Partner, Zeiterfassungs-Konfiguration …), geteilt von allen Komponenten, die
 * ihn schreiben (7.2).
 *
 * Sie hält den Stand (`updatedAt`), die Warteschlange und je Feld die **Basis**:
 * den letzten Wert, den die Maske vom Server kennt. Gegen die Basis wird bei
 * einem 409 `STAND_VERALTET` entschieden, ob jemand **dasselbe** Feld geändert
 * hat (dann fragen) oder nur andere (dann still neu senden).
 *
 * Reine Logik ohne React — die Hooks in `hooks.tsx` hängen sich daran.
 */
import { wertAn } from './pfad'
import { alsText, gleich } from './vergleich'

export type Datensatz = Record<string, unknown>

/** Rückfrage des Servers (heute `PROJECT_CLIENT_CHANGE_WARN`), 3.6. */
export interface Rueckfrage {
  /** Frage an den Benutzer; bekommt den Fehlerkörper des Servers. */
  frage: (koerper: Record<string, unknown>) => string
  /** Wird bei „Ja" zusätzlich gesendet, z. B. `{ confirmClientChange: true }`. */
  kennzeichen: Record<string, unknown>
  ok?: string
}

export interface StelleOptionen<A = unknown> {
  /** `<art>:<id>`, z. B. `project:9f…`; Einstellungen je Mandant ohne id: `timeConfig`. */
  schluessel: string
  /** Für den Hinweis nach dem Wegnavigieren, z. B. „Projekt 00012-26". */
  bezeichnung?: string
  /** Schreibt die Änderung; `stand` gehört in den Header `X-Stand` (siehe `mitStand`). */
  schreibe: (patch: Datensatz, stand: string | undefined) => Promise<A>
  /** Datensatz aus der Antwort (Vorgabe: `antwort.data` bzw. `antwort` selbst). */
  werte?: (antwort: A) => Datensatz
  /** Stand aus der Antwort (Vorgabe: `werte(antwort).updatedAt`). */
  stand?: (antwort: A) => string | undefined
  /** Fehlercode → Rückfrage (3.6). */
  rueckfragen?: Record<string, Rueckfrage>
  /**
   * Ziel für das Senden beim Schließen des Tabs (7.4, `fetch` mit `keepalive`),
   * relativ zur API-Basis. Ohne Ziel warnt der Browser stattdessen.
   */
  keepalive?: { url: string; method: 'PATCH' | 'PUT' }
}

/** Ergebnis eines Auftrags, wie es beim Feld ankommt. */
export type Ergebnis =
  | { art: 'gespeichert' }
  /** Der Benutzer hat eine Rückfrage oder einen Konflikt verneint — das Feld zeigt den Serverwert. */
  | { art: 'verworfen' }
  | { art: 'fehler'; text: string }
  /** Eine neuere Änderung desselben Felds hat diesen wartenden Auftrag ersetzt (7.2). */
  | { art: 'ersetzt' }

export interface Auftrag {
  /** Gleiche Kennung ersetzt den wartenden Auftrag (Feldschlüssel bzw. Gruppen-Id). */
  kennung: string
  /** Die Felder der eigenen Änderung — nur sie zählen beim Konflikt. */
  felder: string[]
  /** Beschriftungen für die Konfliktfrage und den Hinweis. */
  labels?: Record<string, string>
  /** Fester Wert … */
  patch?: Datensatz
  /** … oder eine Operation, die auf den jeweils aktuellen Stand neu angewendet wird (Listen, 8.3 a). */
  operation?: (aktuell: Datensatz) => Datensatz
}

/** Abhängigkeiten, die Tests ersetzen. */
export interface Umgebung {
  frage: (text: string, ok: string) => Promise<boolean>
  fehlerText: (fehler: unknown) => string
  hinweis: (text: string) => void
}

interface Wartend extends Auftrag {
  erledigt: (e: Ergebnis) => void
}

/** Höchstens so oft still neu senden, dann aufgeben — sonst liefe ein Dauerkonflikt endlos. */
const MAX_VERSUCHE = 4

function koerperVon(fehler: unknown): { status?: number; daten?: Record<string, unknown> } {
  const r = (fehler as { response?: { status?: number; data?: unknown } })?.response
  const d = r?.data
  return { status: r?.status, daten: d && typeof d === 'object' ? (d as Record<string, unknown>) : undefined }
}

function vorgabeWerte(antwort: unknown): Datensatz {
  const a = antwort as { data?: unknown } | null
  const innen = a && typeof a === 'object' && a.data && typeof a.data === 'object' && !Array.isArray(a.data) ? a.data : a
  return (innen && typeof innen === 'object' ? innen : {}) as Datensatz
}

export class Speicherstelle<A = unknown> {
  readonly schluessel: string
  private opts: StelleOptionen<A>
  private umgebung: Umgebung

  private standWert: string | undefined
  private basisWerte: Datensatz = {}
  private schlange: Wartend[] = []
  private laeuft = false
  private version = 0
  private hoerer = new Set<() => void>()
  /** Der Datensatz, mit dem `beginne` die Stelle gefüllt hat — solange seither nichts anderes kam. */
  private anfang: A | undefined

  /** Wie viele Komponenten die Stelle gerade benutzen (Register, 7.2). */
  benutzer = 0
  /** Serverfehler je Kennung — zählen für `beforeunload` und den Hinweis (7.4). */
  readonly serverFehler = new Map<string, string>()
  /** Prüffehler je Kennung (3.3) — zählen nur, solange die Maske offen ist. */
  readonly pruefFehler = new Set<string>()
  /** Geänderte, gültige, noch nicht gesendete Eingaben: Kennung → Patch-Erzeuger (7.4). */
  readonly ungesendet = new Map<string, { patch: () => Datensatz | null; keepalive: boolean }>()
  /** Wird gerufen, wenn die Schlange leer ist — das Register räumt dann ab. */
  beiLeer?: () => void

  constructor(opts: StelleOptionen<A>, umgebung: Umgebung) {
    this.schluessel = opts.schluessel
    this.opts = opts
    this.umgebung = umgebung
  }

  /** Neue Optionen (Callbacks ändern sich bei jedem Render) — Stand und Basis bleiben. */
  setzeOptionen(opts: StelleOptionen<A>) {
    this.opts = opts
  }

  get optionen(): StelleOptionen<A> {
    return this.opts
  }

  get stand(): string | undefined {
    return this.standWert
  }

  /** Letzter vom Server bekannter Wert des Felds (auch als Pfad `a.b`). */
  basis(feld: string): unknown {
    return feld.includes('.') ? wertAn(this.basisWerte, feld) : this.basisWerte[feld]
  }

  get daten(): Datensatz {
    return this.basisWerte
  }

  /** Laufende oder wartende Anfragen. */
  get offen(): number {
    return this.schlange.length + (this.laeuft ? 1 : 0)
  }

  get aenderungsZaehler(): number {
    return this.version
  }

  abonniere(fn: () => void): () => void {
    this.hoerer.add(fn)
    return () => this.hoerer.delete(fn)
  }

  private melde() {
    this.version++
    for (const fn of [...this.hoerer]) fn()
  }

  private werte(antwort: A): Datensatz {
    return this.opts.werte ? this.opts.werte(antwort) : vorgabeWerte(antwort)
  }

  private standAus(antwort: A): string | undefined {
    if (this.opts.stand) return this.opts.stand(antwort)
    const u = this.werte(antwort).updatedAt
    return typeof u === 'string' ? u : u instanceof Date ? u.toISOString() : undefined
  }

  /**
   * 7.6 / 7.2 — einen Datensatz übernehmen, den jemand anderes geladen hat
   * (neu geladene Liste, zweite Komponente, Antwort einer Operation). Ein
   * **älterer** Stand wird nicht übernommen.
   */
  uebernimm(datensatz: A) {
    // Damit hat die Stelle begonnen, und seither kam nichts anderes: Es gäbe nichts zu melden.
    if (datensatz === this.anfang) return
    const neu = this.standAus(datensatz)
    if (this.standWert && neu && Date.parse(neu) < Date.parse(this.standWert)) return
    if (this.standWert && neu === this.standWert && this.version > 0) return
    // Ohne Stand (ein Datensatz ohne `updatedAt`) fehlt das Merkmal für „kenne ich schon". Baut ein Aufrufer
    // `daten` bei jedem Zeichnen neu, löste jede Übernahme wieder ein Zeichnen aus — endlos, bis React die
    // Seite abbricht (Posten im Zahlungslauf, 7.10.2026). Was nichts ändert, wird darum nicht gemeldet.
    if (!neu && this.version > 0 && this.bringtNichtsNeues(datensatz)) return
    this.setze(datensatz)
  }

  private bringtNichtsNeues(datensatz: A): boolean {
    const werte = this.werte(datensatz)
    return Object.keys(werte).every((feld) => gleich(werte[feld], this.basisWerte[feld]))
  }

  /**
   * Der erste Datensatz einer **frischen** Stelle — der einzige Weg, der schon
   * beim Zeichnen gegangen werden darf: Es hört noch niemand zu, also wird auch
   * niemand mitten im Render benachrichtigt. `useSpeicherstelle` füllt die Stelle
   * so, bevor das erste Feld sie liest.
   *
   * `false`, wenn die Stelle schon etwas weiß oder jemand zuhört — dann bleibt es
   * bei `uebernimm`.
   */
  beginne(datensatz: A): boolean {
    if (this.version > 0 || this.hoerer.size > 0) return false
    this.setze(datensatz)
    this.anfang = datensatz
    return true
  }

  /**
   * Antwort des Servers ist maßgeblich: Stand und Basis der Felder, die sie
   * trägt. Zusammengeführt, nicht ersetzt — manche PATCH-Antworten tragen
   * weniger Felder als das GET, und die übrigen verlören sonst ihre Basis.
   */
  private setze(antwort: A) {
    this.anfang = undefined
    this.standWert = this.standAus(antwort) ?? this.standWert
    this.basisWerte = { ...this.basisWerte, ...this.werte(antwort) }
    this.melde()
  }

  /** 7.2 — ein Auftrag je Kennung wartet; ein neuer derselben Kennung ersetzt ihn. */
  sende(auftrag: Auftrag): Promise<Ergebnis> {
    return new Promise<Ergebnis>((erledigt) => {
      const i = this.schlange.findIndex((w) => w.kennung === auftrag.kennung)
      if (i >= 0) {
        this.schlange[i].erledigt({ art: 'ersetzt' })
        this.schlange[i] = { ...auftrag, erledigt }
      } else {
        this.schlange.push({ ...auftrag, erledigt })
      }
      this.melde()
      void this.pumpe()
    })
  }

  private async pumpe() {
    if (this.laeuft) return
    const naechster = this.schlange.shift()
    if (!naechster) {
      this.beiLeer?.()
      return
    }
    this.laeuft = true
    let ergebnis: Ergebnis
    try {
      ergebnis = await this.fuehreAus(naechster)
    } catch (e) {
      ergebnis = { art: 'fehler', text: this.umgebung.fehlerText(e) }
    }
    this.laeuft = false
    if (ergebnis.art === 'fehler') {
      this.serverFehler.set(naechster.kennung, ergebnis.text)
      // 7.4: Ist die Maske schon weg, erfährt es der Benutzer einmal.
      if (this.benutzer === 0) {
        const feld = naechster.felder.map((f) => naechster.labels?.[f] ?? f).join(', ')
        this.umgebung.hinweis(
          `${feld}${this.opts.bezeichnung ? ` in ${this.opts.bezeichnung}` : ''} wurde nicht gespeichert: ${ergebnis.text}`,
        )
        this.serverFehler.delete(naechster.kennung)
      }
    } else {
      this.serverFehler.delete(naechster.kennung)
    }
    naechster.erledigt(ergebnis)
    this.melde()
    void this.pumpe()
  }

  private async fuehreAus(auftrag: Wartend): Promise<Ergebnis> {
    let zusatz: Datensatz = {}
    for (let versuch = 1; versuch <= MAX_VERSUCHE; versuch++) {
      const patch = { ...(auftrag.operation ? auftrag.operation(this.basisWerte) : auftrag.patch ?? {}), ...zusatz }
      try {
        const antwort = await this.opts.schreibe(patch, this.standWert)
        this.setze(antwort)
        return { art: 'gespeichert' }
      } catch (fehler) {
        const { status, daten } = koerperVon(fehler)
        const code = typeof daten?.code === 'string' ? daten.code : undefined

        if (status === 409 && code === 'STAND_VERALTET' && daten && 'aktuell' in daten) {
          const aktuell = daten.aktuell as A
          const neu = this.werte(aktuell)
          // Nur die Felder der eigenen Änderung zählen, und zwar gegen die Basis (7.3).
          const konflikt = auftrag.operation ? [] : auftrag.felder.filter((f) => !gleich(wertAn(neu, f), this.basis(f)))
          this.setze(aktuell)
          if (konflikt.length === 0) continue
          const f = konflikt[0]
          const label = auftrag.labels?.[f] ?? f
          const eigener = wertAn(auftrag.patch, f)
          const ja = await this.umgebung.frage(
            `„${label}" wurde inzwischen auf „${alsText(wertAn(neu, f))}" geändert. Deinen Wert „${alsText(eigener)}" speichern?`,
            'Meinen Wert speichern',
          )
          if (!ja) return { art: 'verworfen' }
          continue
        }

        const rueckfrage = code ? this.opts.rueckfragen?.[code] : undefined
        if (status === 409 && rueckfrage && daten) {
          const ja = await this.umgebung.frage(rueckfrage.frage(daten), rueckfrage.ok ?? 'OK')
          if (!ja) return { art: 'verworfen' }
          zusatz = { ...zusatz, ...rueckfrage.kennzeichen }
          continue
        }

        return { art: 'fehler', text: this.umgebung.fehlerText(fehler) }
      }
    }
    return { art: 'fehler', text: 'Der Datensatz wird gerade laufend geändert. Bitte neu laden.' }
  }

  /** Muss der Browser beim Schließen warnen (7.4)? */
  mussWarnen(): boolean {
    if (this.offen > 0 || this.serverFehler.size > 0) return true
    if (this.benutzer > 0 && this.pruefFehler.size > 0) return true
    for (const u of this.ungesendet.values()) if (!u.keepalive || !this.opts.keepalive) return true
    return false
  }

  /** 7.4 — ungesendete Feldänderungen beim Schließen, ohne Stand (S-14). */
  sendeBeimSchliessen(senden: (url: string, method: string, body: string) => void) {
    const ziel = this.opts.keepalive
    if (!ziel) return
    const patch: Datensatz = {}
    for (const u of this.ungesendet.values()) {
      if (!u.keepalive) continue
      Object.assign(patch, u.patch() ?? {})
    }
    if (Object.keys(patch).length === 0) return
    const body = JSON.stringify(patch)
    // Browser nehmen höchstens 64 KB je keepalive-Anfrage (S-21).
    if (body.length > 60_000) return
    senden(ziel.url, ziel.method, body)
    this.ungesendet.clear()
  }
}
